import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FunnelInsightsApiClient } from '../../api/insightsClient';
import type { FunnelSegmentBreakdownData } from '../../types';
import { ExploratoryLabel, StatTierChip } from '../StatTierChip';
import { BREAKDOWN_SECTION_TEST_IDS } from '../../testIds';

export { BREAKDOWN_SECTION_TEST_IDS };

interface FunnelReportsBreakdownSectionProps {
  projectId: string;
  onboardingId?: string;
  versionId?: string;
  from?: string;
  to?: string;
}

/**
 * Cohort/trait breakdown of funnel conversion (AC-007): conversion rate per
 * trait value with explicit numerator/denominator evidence; low-denominator
 * segments render as suppressed instead of misleading percentages.
 */
export const FunnelReportsBreakdownSection: React.FC<FunnelReportsBreakdownSectionProps> = ({
  projectId,
  onboardingId,
  versionId,
  from,
  to,
}) => {
  const client = useMemo(() => new FunnelInsightsApiClient(), []);
  const [traitKeys, setTraitKeys] = useState<string[]>([]);
  const [traitKey, setTraitKey] = useState('');
  const [data, setData] = useState<FunnelSegmentBreakdownData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const controller = new AbortController();
    client.getUserTraits({ projectId, onboardingId, versionId, signal: controller.signal })
      .then(response => setTraitKeys(response.data.traits.map(trait => trait.key)))
      .catch(() => setTraitKeys([]));
    return () => controller.abort();
  }, [client, projectId, onboardingId, versionId]);

  useEffect(() => {
    if (!projectId || !traitKey) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    setError(null);
    client.getReportsBreakdown({
      projectId,
      dimension: `trait:${traitKey}`,
      onboardingId,
      versionId,
      from,
      to,
      signal: controller.signal,
    })
      .then(response => setData(response.data))
      .catch(err => {
        if (controller.signal.aborted) return;
        setData(null);
        setError(err instanceof Error ? err.message : 'Failed to load breakdown');
      });
    return () => controller.abort();
  }, [client, projectId, traitKey, onboardingId, versionId, from, to]);

  if (!traitKeys.length) return null;

  return (
    <Box data-testid={BREAKDOWN_SECTION_TEST_IDS.section} sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2, bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Conversion by audience segment</Typography>
          <Typography variant="caption" color="text.secondary">
            Offer conversion per user-trait value. Rates with fewer than {data?.minDenominator ?? 10} journeys are suppressed.
            {' '}Segment rows are exploratory — many segments mean some will look extreme by chance.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Trait</InputLabel>
          <Select
            label="Trait"
            value={traitKey}
            data-testid={BREAKDOWN_SECTION_TEST_IDS.traitSelect}
            onChange={(event) => setTraitKey(event.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {traitKeys.map(key => <MenuItem key={key} value={key}>{key}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="warning">{error}</Alert>}

      {data && (
        <Table size="small" data-testid={BREAKDOWN_SECTION_TEST_IDS.table}>
          <TableHead>
            <TableRow>
              <TableCell>{traitKey}</TableCell>
              <TableCell align="right">Started journeys</TableCell>
              <TableCell align="right">Converted</TableCell>
              <TableCell align="right">Conversion rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map(row => (
              <TableRow key={`${row.segment}-${row.variantId ?? ''}`} data-testid={BREAKDOWN_SECTION_TEST_IDS.row(row.segment)}>
                <TableCell>{row.segment}{row.variantId ? ` · ${row.variantId}` : ''}</TableCell>
                <TableCell align="right">{row.startedJourneys}</TableCell>
                <TableCell align="right">{row.convertedJourneys}</TableCell>
                <TableCell align="right">
                  {row.suppressed ? (
                    <Tooltip title={`Fewer than ${data.minDenominator} journeys — rate hidden to avoid misleading percentages`}>
                      <Typography
                        variant="body2"
                        color="text.disabled"
                        component="span"
                        data-testid={BREAKDOWN_SECTION_TEST_IDS.suppressedCell(row.segment)}
                      >
                        suppressed ({row.convertedJourneys} / {row.startedJourneys})
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                      <span>
                        {`${((row.conversionRate ?? 0) * 100).toFixed(1)}% (${row.convertedJourneys} / ${row.startedJourneys})`}
                      </span>
                      {row.qualification && <StatTierChip qualification={row.qualification} />}
                      {row.exploratory && <ExploratoryLabel />}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
